import { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Flame, Users, Crown, LogIn, UserPlus, Zap, Target, TrendingUp, Star } from 'lucide-react';
import { gamificationApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { AuthModal } from '@/components/AuthModal';
import { getContentLocale } from '@/lib/contentLocale';

interface LeaderboardUser {
  rank: number;
  userId: string;
  name: string;
  avatarUrl: string | null;
  totalXp: number;
  level: number;
  currentStreak: number;
  isCurrentUser?: boolean;
}

interface UserRanking {
  rank: number | null;
  totalUsers: number;
  percentile: number | null;
  label: string | null;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function mapEntry(entry: any): LeaderboardUser {
  return {
    rank: entry.rank,
    userId: entry.userId,
    name: entry.name || 'Utilisateur',
    avatarUrl: entry.avatarUrl ?? null,
    totalXp: entry.totalXp ?? 0,
    level: entry.level ?? 1,
    currentStreak: entry.currentStreak ?? 0,
  };
}

const RANK_COLORS: Record<number, { bg: string; border: string; text: string; icon: string; glow: string }> = {
  1: { bg: 'from-yellow-500/20 to-amber-600/10', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
  2: { bg: 'from-slate-300/15 to-slate-500/10', border: 'border-slate-400/25', text: 'text-slate-300', icon: 'text-slate-300', glow: 'shadow-slate-400/15' },
  3: { bg: 'from-orange-500/15 to-amber-700/10', border: 'border-orange-500/25', text: 'text-orange-400', icon: 'text-orange-400', glow: 'shadow-orange-500/15' },
};

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <Crown className="w-5 h-5" />;
  if (rank === 2) return <Medal className="w-5 h-5" />;
  if (rank === 3) return <Award className="w-5 h-5" />;
  return null;
}

export function LeaderboardPage() {
  const locale = getContentLocale();
  const { isAuthenticated, user } = useAuthStore();
  const [entries, setEntries] = useState<LeaderboardUser[]>([]);
  const [currentUser, setCurrentUser] = useState<LeaderboardUser | null>(null);
  const [ranking, setRanking] = useState<UserRanking | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [leaderboardRes, rankingRes] = await Promise.all([
          gamificationApi.getLeaderboard(20),
          user?.id ? gamificationApi.getUserRanking(user.id).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        const list: LeaderboardUser[] = (leaderboardRes.data.entries ?? []).map(mapEntry);
        const me = leaderboardRes.data.currentUser ? mapEntry(leaderboardRes.data.currentUser) : null;

        setEntries(list);
        setCurrentUser(me);
        setRanking(rankingRes?.data ?? null);
      } catch {
        if (!cancelled) {
          setEntries([]);
          setCurrentUser(null);
          setRanking(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  const currentUserId = currentUser?.userId ?? user?.id;

  const t = {
    title: locale === 'en' ? 'Leaderboard' : 'Classement',
    subtitle: locale === 'en' ? 'Compete with other learners' : 'Affrontez les autres apprenants',
    yourRank: locale === 'en' ? 'Your rank' : 'Votre rang',
    users: locale === 'en' ? 'participants' : 'participants',
    pts: 'XP',
    level: locale === 'en' ? 'Lv.' : 'Niv.',
    you: locale === 'en' ? 'You' : 'Vous',
    noRank: locale === 'en' ? 'Not ranked yet' : 'Pas encore classé',
    noRankSub: locale === 'en' ? 'Complete a quiz to join the leaderboard' : 'Complétez un quiz pour rejoindre le classement',
    empty: locale === 'en' ? 'No rankings yet' : 'Aucun classement disponible',
    emptySub: locale === 'en' ? 'Be the first to complete a quiz!' : 'Soyez le premier à compléter un quiz !',
    loginTitle: locale === 'en' ? 'Track your ranking' : 'Suivez votre classement',
    loginSub: locale === 'en' ? 'Sign in to see your rank and compete with others' : 'Connectez-vous pour voir votre rang et défier les autres',
    login: locale === 'en' ? 'Sign in' : 'Se connecter',
    signup: locale === 'en' ? 'Create account' : 'Créer un compte',
    streak: locale === 'en' ? 'd' : 'j',
    howItWorks: locale === 'en' ? 'How ranking works' : 'Comment ça marche',
    howDesc: locale === 'en'
      ? 'Your ranking is based on experience points (XP) earned through practice.'
      : 'Votre classement est basé sur les points d\'expérience (XP) gagnés en pratiquant.',
    earnXp: locale === 'en' ? 'Earn XP' : 'Gagnez des XP',
    earnXpDesc: locale === 'en'
      ? 'Complete quizzes and simulations to earn experience points.'
      : 'Complétez des quiz et simulations pour gagner des points d\'expérience.',
    climbRanks: locale === 'en' ? 'Climb the ranks' : 'Montez en rang',
    climbRanksDesc: locale === 'en'
      ? 'The more XP you accumulate, the higher your position.'
      : 'Plus vous accumulez d\'XP, plus votre position monte.',
    keepStreak: locale === 'en' ? 'Keep your streak' : 'Gardez votre série',
    keepStreakDesc: locale === 'en'
      ? 'Practice daily to maintain and grow your streak.'
      : 'Pratiquez chaque jour pour maintenir et augmenter votre série.',
    levelUp: locale === 'en' ? 'Level up' : 'Montez de niveau',
    levelUpDesc: locale === 'en'
      ? 'Unlock new levels as you gain more XP and achievements.'
      : 'Débloquez de nouveaux niveaux en gagnant plus d\'XP et de succès.',
    xpGuide: locale === 'en' ? 'XP rewards' : 'Récompenses XP',
    quizComplete: locale === 'en' ? 'Quiz completed' : 'Quiz complété',
    simComplete: locale === 'en' ? 'Simulation completed' : 'Simulation complétée',
    perfectScore: locale === 'en' ? 'Perfect score bonus' : 'Bonus score parfait',
    dailyStreak: locale === 'en' ? 'Daily streak bonus' : 'Bonus série quotidienne',
  };

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-5 sm:pt-6 pb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-500/20 to-amber-600/20 rounded-xl flex items-center justify-center border border-yellow-500/15">
            <Trophy className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-fg">{t.title}</h1>
            <p className="text-xs text-fg-faint mt-0.5">{t.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6 pt-3">
        {/* Desktop two-column layout */}
        <div className="flex gap-6">
          {/* Main column — leaderboard */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Auth CTA for unauthenticated users */}
                {!isAuthenticated && (
                  <div className="mb-5 bg-gradient-to-br from-emerald-500/10 via-green-500/5 to-transparent border border-emerald-500/15 rounded-2xl p-5 sm:p-6">
                    <div className="flex flex-col items-center text-center gap-3">
                      <div className="w-12 h-12 bg-emerald-500/15 rounded-full flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-fg">{t.loginTitle}</p>
                        <p className="text-xs text-fg-subtle mt-1 max-w-xs">{t.loginSub}</p>
                      </div>
                      <div className="flex gap-3 mt-1">
                        <button
                          onClick={() => setShowAuth(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold rounded-xl transition-colors"
                        >
                          <LogIn className="w-3.5 h-3.5" />
                          {t.login}
                        </button>
                        <button
                          onClick={() => setShowAuth(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-overlay-2 hover:bg-overlay-3 text-fg-muted text-xs font-medium rounded-xl border border-line transition-colors"
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          {t.signup}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current user rank card */}
                {isAuthenticated && ranking?.label && (
                  <div className="mb-5 bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-transparent border border-yellow-500/15 rounded-2xl p-4 sm:p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-yellow-500/15 rounded-full flex items-center justify-center">
                          <Crown className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <p className="text-[10px] sm:text-xs text-fg-subtle uppercase tracking-wider font-medium">{t.yourRank}</p>
                          <p className="text-2xl sm:text-3xl font-black text-yellow-400 leading-tight">{ranking.label}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-fg-faint">{ranking.totalUsers} {t.users}</p>
                        {ranking.percentile !== null && (
                          <p className="text-[10px] text-yellow-400/60 mt-0.5">Top {100 - ranking.percentile}%</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {isAuthenticated && !ranking?.label && (
                  <div className="mb-5 bg-overlay-1 border border-line rounded-2xl p-4 sm:p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-overlay-1 rounded-full flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-fg-faint" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-fg-subtle">{t.noRank}</p>
                        <p className="text-xs text-fg-faint mt-0.5">{t.noRankSub}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Leaderboard list */}
                {entries.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-fg-faint">
                    <Users className="w-12 h-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">{t.empty}</p>
                    <p className="text-xs mt-1 text-fg-faint">{t.emptySub}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {entries.map((entry) => {
                      const isYou = entry.userId === currentUserId;
                      const rankStyle = RANK_COLORS[entry.rank];
                      const isTopThree = entry.rank <= 3;

                      return (
                        <div
                          key={entry.userId}
                          className={`
                            flex items-center gap-3 p-3 sm:p-3.5 rounded-xl border transition-all
                            ${isYou
                              ? 'bg-gradient-to-r from-emerald-500/12 to-emerald-500/5 border-emerald-500/20 shadow-lg shadow-emerald-500/5'
                              : isTopThree && rankStyle
                                ? `bg-gradient-to-r ${rankStyle.bg} ${rankStyle.border} shadow-lg ${rankStyle.glow}`
                                : 'bg-surface border-line hover:border-line'
                            }
                          `}
                        >
                          {/* Rank */}
                          <div className={`w-8 sm:w-9 flex-shrink-0 flex items-center justify-center ${
                            isTopThree && rankStyle ? rankStyle.icon : 'text-fg-faint'
                          }`}>
                            {isTopThree ? (
                              <RankIcon rank={entry.rank} />
                            ) : (
                              <span className="text-xs sm:text-sm font-bold">{entry.rank}</span>
                            )}
                          </div>

                          {/* Avatar */}
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                            isYou
                              ? 'bg-emerald-500/20 text-emerald-400 ring-2 ring-emerald-500/30'
                              : isTopThree && rankStyle
                                ? `bg-overlay-3 ${rankStyle.text} ring-2 ${rankStyle.border}`
                                : 'bg-overlay-1 text-fg-subtle'
                          }`}>
                            {getInitials(entry.name)}
                          </div>

                          {/* Name & XP */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-semibold truncate ${
                                isYou ? 'text-emerald-400' : 'text-fg'
                              }`}>
                                {isYou ? t.you : entry.name}
                              </p>
                              {isYou && (
                                <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded font-medium">
                                  {t.you}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-fg-faint font-medium">
                                {entry.totalXp.toLocaleString()} {t.pts}
                              </span>
                              <span className="text-[10px] text-fg-faint">·</span>
                              <span className="text-[10px] text-fg-faint">
                                {t.level} {entry.level}
                              </span>
                            </div>
                          </div>

                          {/* Streak */}
                          {entry.currentStreak > 0 && (
                            <div className="flex items-center gap-1 flex-shrink-0 bg-orange-500/10 px-2 py-1 rounded-lg">
                              <Flame className="w-3 h-3 text-orange-400" />
                              <span className="text-[10px] sm:text-xs text-orange-400 font-medium">
                                {entry.currentStreak}{t.streak}
                              </span>
                            </div>
                          )}

                          {/* Rank badge for top 3 */}
                          {isTopThree && rankStyle && (
                            <div className="flex-shrink-0 text-right">
                              <span className={`text-sm font-bold ${rankStyle.text}`}>
                                #{entry.rank}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Show current user at bottom if not in list */}
                    {currentUser && !entries.some((e) => e.userId === currentUserId) && (
                      <>
                        <div className="flex items-center gap-2 py-2">
                          <div className="flex-1 h-px bg-overlay-2" />
                          <span className="text-[10px] text-fg-faint">···</span>
                          <div className="flex-1 h-px bg-overlay-2" />
                        </div>
                        <div className="flex items-center gap-3 p-3 sm:p-3.5 rounded-xl border bg-gradient-to-r from-emerald-500/12 to-emerald-500/5 border-emerald-500/20 shadow-lg shadow-emerald-500/5">
                          <div className="w-8 sm:w-9 flex-shrink-0 text-center">
                            <span className="text-xs sm:text-sm font-bold text-emerald-400">{currentUser.rank}</span>
                          </div>
                          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-full flex items-center justify-center text-[11px] font-bold text-emerald-400 flex-shrink-0 ring-2 ring-emerald-500/30">
                            {getInitials(currentUser.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-emerald-400">{t.you}</p>
                              <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded font-medium">
                                #{currentUser.rank}
                              </span>
                            </div>
                            <span className="text-xs text-fg-faint font-medium">
                              {currentUser.totalXp.toLocaleString()} {t.pts}
                            </span>
                          </div>
                          {currentUser.currentStreak > 0 && (
                            <div className="flex items-center gap-1 flex-shrink-0 bg-orange-500/10 px-2 py-1 rounded-lg">
                              <Flame className="w-3 h-3 text-orange-400" />
                              <span className="text-[10px] sm:text-xs text-orange-400 font-medium">
                                {currentUser.currentStreak}{t.streak}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Side card — desktop only */}
          <div className="hidden xl:flex flex-col gap-4 w-[320px] flex-shrink-0">
            {/* How it works */}
            <div className="bg-surface border border-line rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-1">{t.howItWorks}</h3>
              <p className="text-xs text-fg-faint mb-4">{t.howDesc}</p>

              <div className="space-y-3.5">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-yellow-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-fg">{t.earnXp}</p>
                    <p className="text-[11px] text-fg-faint mt-0.5 leading-relaxed">{t.earnXpDesc}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-fg">{t.climbRanks}</p>
                    <p className="text-[11px] text-fg-faint mt-0.5 leading-relaxed">{t.climbRanksDesc}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Flame className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-fg">{t.keepStreak}</p>
                    <p className="text-[11px] text-fg-faint mt-0.5 leading-relaxed">{t.keepStreakDesc}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-fg">{t.levelUp}</p>
                    <p className="text-[11px] text-fg-faint mt-0.5 leading-relaxed">{t.levelUpDesc}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* XP rewards guide */}
            <div className="bg-surface border border-line rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-yellow-400" />
                {t.xpGuide}
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-subtle">{t.quizComplete}</span>
                  <span className="text-xs font-semibold text-yellow-400">+10 XP</span>
                </div>
                <div className="h-px bg-overlay-1" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-subtle">{t.simComplete}</span>
                  <span className="text-xs font-semibold text-yellow-400">+25 XP</span>
                </div>
                <div className="h-px bg-overlay-1" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-subtle">{t.perfectScore}</span>
                  <span className="text-xs font-semibold text-emerald-400">+15 XP</span>
                </div>
                <div className="h-px bg-overlay-1" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-fg-subtle">{t.dailyStreak}</span>
                  <span className="text-xs font-semibold text-orange-400">+5 XP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAuth && <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />}
    </div>
  );
}
